/**
 * Rig containers — portable stateful objects.
 *
 * A **rig container** is a plain structural SceneObject inserted as a stateful
 * object's parent. The container adopts the object's authored local pose
 * ("where the rig sits"), and the object is reset to identity relative to the
 * container ("the base state"). Creation is LAZY: an object's states live on
 * the object itself until a rig is actually needed — when a TRANSLATION is
 * keyed into the owner's state (`promoteToRigOwner` in statesActions) or when
 * new content joins the state context (`ensureStateContextContainer`).
 * Value/visibility/rotation/scale states never create a rig. Splitting
 * placement from base state matters because the object's own transform used to
 * conflate the two meanings, so:
 *
 *   - moving the container moves the whole rig (its states follow), and
 *   - moving the object edits the base state (its states stay put).
 *
 * With absolute-local state transforms (ABSOLUTE_STATE_TRANSFORMS.md) this is
 * what makes duplicate-then-move portable *by construction*: the object's state
 * poses are relative to its own (container-local) origin, so they travel with
 * the container. See STATE_RIG_CONTAINERS.md.
 *
 * The wrap is transform-preserving: the container starts at the object's old
 * pose and the object drops to identity, so the object's WORLD pose is
 * unchanged and no state poses are rewritten. Runtime is untouched — a
 * container is an ordinary structural SceneObject.
 *
 * This module is an EDITOR concern only. Nothing here runs at load or
 * instantiation time; a stateful object without a container is still valid and
 * portable-in-context — it just doesn't offer the separate move-rig gesture.
 *
 * Relative interaction-target paths into/out of the wrapped subtree change
 * depth on wrap/unwrap. This module does NO path bookkeeping — the
 * reference-identity registry (referenceIdentity.ts, installed once per host
 * session) re-derives them from identity on the topology signal these
 * structural changes emit.
 */

import { Quaternion, Vector3, Matrix } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { TransformData } from '../../scenedata/transformdata';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import { ActionCommand } from '../undo/ActionCommand';
import { DeleteObjectCommand } from '../undo/DeleteObjectCommand';
import type { UndoCommand } from '../undo/UndoCommand';
import { Logger } from '../../utils/Logger';

/** Identity local transform — the object's pose relative to its new container. */
const IDENTITY_TRANSFORM: TransformData = {
  translation: [0, 0, 0],
  rotation: [0, 0, 0, 1],
  scale: [1, 1, 1],
};

/** Suffix appended to an object's name to derive its rig container's name. */
const RIG_SUFFIX = ' Rig';

/** Derive a rig container name from the wrapped object's name. */
function rigNameFor(objName: string): string {
  return `${objName}${RIG_SUFFIX}`;
}

/**
 * Sibling-unique rig name. The rig name is minted deterministically from the
 * object's name, so "rename member, reuse its old name on a new object, wrap
 * it" would collide with the first rig. createSceneObjectFromData would dedupe
 * that as "<name> Rig (1)", which loses the suffix, so this disambiguates first
 * as "<name> (N) Rig": suffix-PRESERVING, so isRigContainer's
 * `endsWith(' Rig')` recognition still holds for deduped rigs.
 */
function uniqueRigNameFor(objName: string, parent: SceneObject): string {
  const siblings = new Set(parent.getChildren().map((c) => c.name));
  const base = rigNameFor(objName);
  if (!siblings.has(base)) return base;
  for (let i = 1; ; i++) {
    const candidate = rigNameFor(`${objName} (${i})`);
    if (!siblings.has(candidate)) return candidate;
  }
}

/**
 * The name to recreate a rig under on undo or redo: the one it had, or, if a
 * sibling has taken it since, a new one from {@link uniqueRigNameFor} for its
 * member.
 */
function freeRigName(name: string, member: SceneObject, parent: SceneObject): string {
  return parent.getChildren().some((child) => child.name === name) ? uniqueRigNameFor(member.name, parent) : name;
}

/**
 * True when `obj` is itself a rig container — a src-less structural node whose
 * name ends with the rig suffix. Rig containers are the STABLE FRAME for their
 * members' state poses, so callers must NOT key the rig's OWN transform into a
 * state (that would pin the rig to an absolute spot and break portability on
 * duplicate). Only members carry posed state transforms, relative to the rig.
 */
export function isRigContainer(obj: SceneObject): boolean {
  return !obj.getSceneObjectData().src && obj.name.endsWith(RIG_SUFFIX);
}

/**
 * True when `obj` already sits directly under a rig container this module would
 * have created — i.e. its parent is a structural (src-less) object named
 * `<obj> Rig`, or the sibling-deduped form `<obj> (N) Rig` that
 * {@link uniqueRigNameFor} mints on a name collision. Used wherever the rig
 * must be `obj`'s OWN (wrap, unwrap, promotion, demotion). Owner resolution also
 * accepts a marked rig wrapping a sibling, so an object added beside the
 * rig's namesake joins the same group.
 *
 * The deduped form MUST be recognized: `promoteToRigOwner` gates on this
 * predicate, so an exact-name-only match made the second same-named object's
 * promotion bail AFTER the wrap — a stateless rig with the hoist silently
 * skipped (and the wrap leaked out of the undo bundle).
 *
 * NOTE: the rig carries a Properties behavior (it OWNS the group's states), so
 * this must NOT require the parent to be behavior-less — only src-less (it's a
 * pure grouping node, never an asset) and name-matched.
 */
export function hasRigContainer(obj: SceneObject): boolean {
  const parent = obj.getParent();
  if (!parent) return false;
  const data = parent.getSceneObjectData();
  if (data.src) return false;
  if (parent.name === rigNameFor(obj.name)) return true;
  // Deduped form: "<obj.name> (N) Rig" with N all digits.
  const prefix = `${obj.name} (`;
  const suffix = `)${RIG_SUFFIX}`;
  if (!parent.name.startsWith(prefix) || !parent.name.endsWith(suffix)) return false;
  const n = parent.name.slice(prefix.length, parent.name.length - suffix.length);
  return /^\d+$/.test(n);
}

/**
 * Snapshot an object's current authored local transform as plain data. Reads
 * from sceneData (the authored rest pose), not the live node — the live node
 * may reflect a state override that hasn't been folded into authored.
 */
function authoredTransform(obj: SceneObject): TransformData {
  const t = obj.getSceneObjectData().transform ?? {};
  return {
    translation: t.translation ? [...t.translation] : [0, 0, 0],
    rotation: t.rotation ? [...t.rotation] : [0, 0, 0, 1],
    scale: t.scale ? [...t.scale] : [1, 1, 1],
  };
}

/**
 * Wrap `obj` in a fresh rig container (its new parent), preserving world pose.
 *
 * Returns an undoable {@link UndoCommand} that the caller should bundle with the
 * state edit that triggered the wrap (so one Ctrl+Z reverses both). Returns
 * `null` when no wrap is needed (already wrapped) or the object can't be wrapped
 * (no scene root, or a Pages container's direct child). The command is NOT
 * pushed — the caller owns undo bundling.
 *
 * On success the object has been reparented under the container and reset to
 * identity by the time this resolves; the returned command's `redo()` re-applies
 * that same structure.
 */
export async function wrapInRigContainer(obj: SceneObject): Promise<UndoCommand | null> {
  if (hasRigContainer(obj)) return null;
  // A Pages container reads its direct children as its pages, by position, and
  // its shared content, by path. A rig between them would turn shared content
  // into a page and shift `pages[]`, so the object keeps its states unwrapped.
  if (obj.getParent()?.getBehavior(Pages)) return null;

  const scene = obj.traceScene;
  // Wrapping is a best-effort editor affordance: if the host scene can't create
  // or reparent objects (headless/minimal contexts), skip the wrap and let the
  // state edit proceed unwrapped. A stateful object without a container is still
  // valid — it just doesn't get the separate move-rig gesture.
  if (typeof scene.createSceneObjectFromData !== 'function'
    || typeof scene.reparentSceneObject !== 'function') {
    return null;
  }

  // The destination for both the container (on wrap) and the object (on unwrap)
  // is a concrete SceneObject — either the object's current parent or, at the
  // top level, the scene root. reparentSceneObject requires a non-null parent.
  const originalParent = obj.getParent() ?? scene.getSceneRoot();
  if (!originalParent) {
    Logger.warn(`[rigContainer] No scene root — cannot wrap ${obj.name}`);
    return null;
  }
  const pose = authoredTransform(obj);

  // Snapshot the container's data ONCE so undo/redo cycles recreate the same
  // container even if the wrapped object is renamed in between. The name is
  // deduped against existing siblings (see uniqueRigNameFor).
  //
  // The rig is born with a MARKED Properties behavior (`stateGroup`), so it is a
  // group boundary from the instant it exists — before any state is added.
  // Ownership is data (the marker), not a name/structure heuristic, so
  // `isGroupBoundary` is a pure marker read with no rig special-case. Baking the
  // behavior into `containerData` means undo/redo recreation preserves the marker
  // for free (no separate bundled Properties-add). The later
  // `ensureStatesObjectCommand(rig)` is idempotent — it returns this existing
  // Properties instead of adding a second. See STATE_GROUP_BOUNDARIES.md.
  const containerData: SceneObjectData = {
    name: uniqueRigNameFor(obj.name, originalParent),
    active: true,
    transform: { ...pose, parent: originalParent.name },
    behaviors: [{ '@type': 'Properties', properties: [], states: {}, stateGroup: true } as BehaviorData],
  };

  // Identity ref for the container, shared with every other undo command that
  // targets it (the bundled Properties add/commit). unwrap()/rewrap() rebind it
  // across delete/re-create cycles so those sibling commands keep resolving the
  // LIVE instance instead of a disposed one (mirrors CreateObjectCommand).
  let containerRef: SceneObjectRef | null = null;

  const apply = async (): Promise<SceneObject> => {
    const container = await scene.createSceneObjectFromData(
      { ...containerData, name: freeRigName(containerData.name, obj, originalParent) },
      originalParent,
    );
    if (containerRef) scene.objectIdentityRegistry.rebind(containerRef, container);
    else containerRef = scene.objectIdentityRegistry.refFor(container);

    // Reparent the object under the container and drop it to identity: the
    // container now carries the placement, so the object's local pose becomes
    // the base state (at the container's origin). setParent is a raw parent
    // assignment (no world-preserving adjust), so resetting to identity here is
    // what keeps the world pose unchanged.
    scene.reparentSceneObject(obj, container);
    applyLocalPose(obj, IDENTITY_TRANSFORM);
    return container;
  };

  const container = await apply();
  Logger.log(`[rigContainer] Wrapped ${obj.name} in ${container.name}`);

  const unwrap = (): void => {
    // Resolve the container via its identity ref — NOT obj.getParent(), which
    // may be an unrelated node if the object was reparented since the wrap
    // (reparents are not undo-tracked). Never delete anything but the exact
    // container this command created, and only when it holds no other content.
    const target = containerRef?.get() ?? null;
    if (!target) return;
    if (obj.getParent() === target) {
      scene.reparentSceneObject(obj, originalParent);
      applyLocalPose(obj, pose);
    }
    // Content added under the rig after the wrap (assets nest under the state
    // root) must survive undo: keep a non-empty container, delete only an
    // empty one. The ref stays bound to a kept container so redo reuses it.
    if (target.getChildren().length === 0) {
      scene.deleteSceneObject(target);
      scene.objectIdentityRegistry.rebind(containerRef!, null);
    }
  };

  const rewrap = async (): Promise<void> => {
    // If unwrap kept the container (it had other children), reuse it instead
    // of creating a duplicate.
    const existing = containerRef?.get() ?? null;
    if (existing) {
      if (obj.getParent() !== existing) {
        scene.reparentSceneObject(obj, existing);
        applyLocalPose(obj, IDENTITY_TRANSFORM);
      }
      return;
    }
    await apply();
  };

  return new ActionCommand(`Wrap ${obj.name} in rig`, unwrap, rewrap);
}

/**
 * Compose `local` on top of `parent` (both plain TransformData): the pose the
 * local frame occupies in the PARENT's parent frame. Babylon composes
 * world = local × parentWorld, so this is Mlocal × Mparent, decomposed.
 * Used by the unwrap fold: a member's new authored pose after its rig is
 * removed is memberPose ∘ rigPose.
 */
function composeTransforms(local: TransformData, parent: TransformData): TransformData {
  const compose = (t: TransformData): Matrix => Matrix.Compose(
    Vector3.FromArray(t.scale ?? [1, 1, 1]),
    Quaternion.FromArray(t.rotation ?? [0, 0, 0, 1]),
    Vector3.FromArray(t.translation ?? [0, 0, 0]),
  );
  const m = compose(local).multiply(compose(parent));
  const scl = new Vector3();
  const rot = new Quaternion();
  const pos = new Vector3();
  m.decompose(scl, rot, pos);
  return {
    translation: [pos.x, pos.y, pos.z],
    rotation: [rot.x, rot.y, rot.z, rot.w],
    scale: [scl.x, scl.y, scl.z],
  };
}

/**
 * Inverse of {@link wrapInRigContainer}: remove a rig whose ONLY child is the
 * member it was created for, folding the rig's placement back into the
 * member's authored pose (world pose unchanged) and deleting the rig node.
 *
 * The DEMOTION half of the lazy-rig lifecycle: a rig exists to carry keyed
 * translations / peer content; when the last state is deleted (see
 * `deleteStateForSubtree`) it is pure outliner noise. Callers gate on the
 * rig's CONTENT (no declarations, no non-Properties behaviors, no remaining
 * member transform overrides) — this primitive checks only structure: exactly
 * one child, recognized rig shape.
 *
 * Returns an undoable command for the caller to bundle (never pushes); the
 * unwrap has already executed on return. Undo recreates the rig (rebinding
 * its shared identity ref, so bundled sibling commands targeting the rig —
 * e.g. the state-delete Properties commit — keep resolving the live
 * instance) and restores the member's original pose under it. Returns null
 * when the host can't restructure (headless) or the shape doesn't match.
 */
export async function unwrapRigContainer(rig: SceneObject): Promise<UndoCommand | null> {
  const scene = rig.traceScene;
  if (typeof scene.createSceneObjectFromData !== 'function'
    || typeof scene.reparentSceneObject !== 'function') {
    return null;
  }
  const children = rig.getChildren();
  if (children.length !== 1) return null;
  const member = children[0];
  if (!hasRigContainer(member)) return null; // only unwrap rigs this module made

  // NOTE: raw ref, not an identity ref — safe because no bundled sibling in
  // the demotion flow deletes/recreates the rig's PARENT. Revisit if a future
  // caller composes unwrap with commands that touch it.
  const rigParent = rig.getParent() ?? scene.getSceneRoot();
  if (!rigParent) return null;

  // Snapshots at unwrap time. Callers run this AFTER any Properties commits
  // (the last-state delete), so authored data — including the rig's now-empty
  // states scaffold — is current; undo recreates exactly this rig and the
  // bundled Properties command restores the states on it afterwards.
  const rigData = structuredClone(rig.getSceneObjectData());
  const rigPose = authoredTransform(rig);
  const memberPose = authoredTransform(member);
  const folded = composeTransforms(memberPose, rigPose);

  // Shared identity ref: the same instance-keyed ref any bundled sibling
  // command holds for the rig; rebound across the delete/recreate cycles.
  const rigRef: SceneObjectRef = scene.objectIdentityRegistry.refFor(rig);

  const doUnwrap = (): void => {
    const target = rigRef.get();
    if (!target) return;
    if (member.getParent() === target) {
      scene.reparentSceneObject(member, rigParent);
      applyLocalPose(member, folded);
    }
    if (target.getChildren().length === 0) {
      scene.deleteSceneObject(target);
      scene.objectIdentityRegistry.rebind(rigRef, null);
    }
  };

  const rewrap = async (): Promise<void> => {
    let container = rigRef.get();
    if (!container) {
      container = await scene.createSceneObjectFromData(
        { ...rigData, name: freeRigName(rigData.name, member, rigParent) },
        rigParent,
      );
      scene.objectIdentityRegistry.rebind(rigRef, container);
    }
    if (member.getParent() !== container) {
      scene.reparentSceneObject(member, container);
      applyLocalPose(member, memberPose);
    }
  };

  doUnwrap();
  Logger.log(`[rigContainer] Unwrapped ${member.name} from ${rigData.name}`);

  return new ActionCommand(`Unwrap ${rigData.name}`, rewrap, doUnwrap);
}

/**
 * Delete a rig container left EMPTY by a host delete flow (deleting the sole
 * wrapped child used to strand a stateful-but-empty `<name> Rig`). Call with
 * the deleted object's former parent; no-op (null) unless it is a childless
 * rig. The delete has executed on return; the returned command is for the
 * caller to bundle with its own delete entry.
 *
 * ORDER MATTERS in the bundle: place this command AFTER the delete that
 * emptied the rig (`[childDeleteCmd, pruneCmd]`). CompositeCommand.undo
 * reverses children, so the prune's undo (recreating the rig and rebinding
 * its shared identity ref) runs FIRST, and the child's undo then re-nests
 * under the live rig. Bundled the other way, the child's undo would resolve
 * the rig's ref before anything rebinds it — the commands' disposed-parent
 * guard degrades that to a scene-root fallback rather than reparenting under
 * a dead node, but the hierarchy is still wrong. Keep the order.
 */
export function pruneEmptyRig(parent: SceneObject | null | undefined): UndoCommand | null {
  if (!parent || !isRigContainer(parent)) return null;
  if (parent.getChildren().length > 0) return null;
  const scene = parent.traceScene;
  if (typeof scene.deleteSceneObject !== 'function') return null;
  // Construction-before-delete invariant (see DeleteObjectCommand header).
  const rigName = parent.name;
  const cmd = new DeleteObjectCommand(
    parent,
    [{ object: parent, data: parent.getSceneObjectData(), parent: parent.getParent() }],
    parent.getParent(),
  );
  // Acquire the shared ref while the rig is still live, delete, then unbind —
  // parity with DeleteObjectCommand.redo(): the direct delete must not leave
  // the ref bound to a disposed instance, so any sibling resolving it before
  // our undo rebinds gets a deterministic "unbound" (scene-root fallback),
  // not a dead node.
  const rigRef = scene.objectIdentityRegistry.refFor(parent);
  scene.deleteSceneObject(parent);
  scene.objectIdentityRegistry.rebind(rigRef, null);
  Logger.log(`[rigContainer] Pruned empty rig ${rigName}`);
  return cmd;
}

/**
 * Write a local transform onto both the live Babylon node and authored
 * sceneData, then fold it in so a subsequent state capture reads the new
 * authored pose (not a stale one).
 */
function applyLocalPose(obj: SceneObject, t: TransformData): void {
  const [px, py, pz] = t.translation ?? [0, 0, 0];
  const [rx, ry, rz, rw] = t.rotation ?? [0, 0, 0, 1];
  const [sx, sy, sz] = t.scale ?? [1, 1, 1];

  const node = obj.transform;
  node.position.set(px, py, pz);
  node.rotationQuaternion = new Quaternion(rx, ry, rz, rw);
  node.scaling.set(sx, sy, sz);

  // Fold the live pose into authored sceneData. captureTransform reads the
  // Properties authored-transform if present; reset it first so it reads the
  // live node we just set, keeping authored in sync (mirrors the gizmo path).
  obj.getBehavior(Properties)?.resetSavedTransform();
  obj.captureTransform();
  obj.refreshSceneData();
}
